import {
  all,
  call,
  fork,
  put,
  select,
  takeLatest,
  take,
} from 'redux-saga/effects';

import { getFieldPermission } from './sharingSaga';
import {
  REQUEST_APP_INITIALIZER,
  RECEIVE_FEATURES,
} from '../constants/actionTypes';
import {
  MICROSOFT_WEATHER_GDD,
  DMB_CROP_SEASON_FILTER,
} from '../constants/featureFlags';
import { reqSoilSsurgoZones } from '../actions/mosaicActions';

import { appInitializerFailure } from '../actions/appInitializerActions';
import {
  receiveSelectorVals,
  fieldNotFoundFailure,
} from '../actions/selectorActions';
import { receivePlantingAnalytics } from '../actions/plantingActions';

import { extractSeasons, extractMosaicSeasons } from '../utils/seasonData';
import { extractMapLayers } from '../utils/layerData';
import { reqActivities } from '../actions/scoutingActions';
import { reqObservations } from '../actions/weatherActions';
import {
  getActiveSelectorVals,
  determineRequestedSelectorIds,
  updateSelectorIdsInStorage,
} from '../utils/selectorOptions';
import { logDmbError } from '../utils/error';
import { getFieldsMultiOperation } from './fieldsMultiOperSaga';
import { getCropList, getCropMap} from '../sagas/cropSaga';
import { FeatureFlagKey } from '../types';
import { getSeasonTagWithWildcards } from '../utils/seasonUtils';

import { getUserData } from './appInitializerSaga/userData';
import { fieldDeps as legacyFieldDeps } from './appInitializerSaga/legacyActivities';
import { fieldDeps as newFieldDeps } from './appInitializerSaga/activities';
import {
  getHarvestArdDataFeatureSelector,
  featureFlagsAreFetching,
  getFeatureValue,
} from '../selectors/featureSelectors';
import { fetchFieldSearchIndex } from '../thunks/uapiFieldsThunks';
import type { SagaType } from './types';

interface SeasonEntry {
  season: string;
}

interface AppInitializerAction {
  type: string;
  fieldId: string;
  mapLayerIds: string[];
  seasonIds: string[];
}

export function* getAppInitializer(action: AppInitializerAction) {
  const { fields, currentUser } = yield select((store: Record<string, unknown>) => store);
  const {
    fieldId,
    mapLayerIds,
    seasonIds,
  } = action;

  const { hasFetched } = currentUser;

  if (!hasFetched) {
    yield call(getUserData);
  }

  let featureFlagsLoading = yield select(featureFlagsAreFetching);
  while (featureFlagsLoading) {
    yield take(RECEIVE_FEATURES);
    featureFlagsLoading = yield select(featureFlagsAreFetching);
  }

  const fieldsIds = fields?.allIds;

  let cropMap: Record<string, unknown>;
  let fieldPermission: string;
  let harvestSeasons: SeasonEntry[] | null;
  let mosaicData: Record<string, Record<string, unknown>>;
  let precPlantingSeasons: { seasonList: SeasonEntry[] | null; liquidSeasons: SeasonEntry[] };
  let plantingSeasons: SeasonEntry[] | null;
  let productAssets: Record<string, unknown>;
  let plantingEvents: { events: Record<string, unknown>; seasons: SeasonEntry[]; plantingSeasons?: SeasonEntry[] };

  try {
    const isHarvestArdDataEnabled = yield select(getHarvestArdDataFeatureSelector);

    const isDmbFieldRetrievalMigrationEnabled = yield select(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (state: any) =>
        getFeatureValue(state, { key: FeatureFlagKey.DMB_FIELD_RETRIEVAL_MIGRATION })
    );

    if (fieldsIds?.length === 0 && isDmbFieldRetrievalMigrationEnabled) {
      const allIds: string[] = yield select(
        (state: { operations: { allIds: string[] } }) => state.operations.allIds
      );
      const operationIds = allIds.filter(id => id !== 'all-operations');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      yield put(fetchFieldSearchIndex(operationIds) as any);
    }

    const fieldDepsCall = isHarvestArdDataEnabled ?
      call(newFieldDeps, fieldId) :
      call(legacyFieldDeps, fieldId);

    let fieldDepsValues: Record<string, unknown>;
    [cropMap, fieldDepsValues, fieldPermission ] = yield all([
      call(getCropMap),
      fieldDepsCall,
      call(getFieldPermission, fieldId),
    ]);

    harvestSeasons = fieldDepsValues.harvestSeasons as SeasonEntry[] | null;
    mosaicData = fieldDepsValues.mosaicData as Record<string, Record<string, unknown>>;
    plantingSeasons = fieldDepsValues.plantingSeasons as SeasonEntry[] | null;
    precPlantingSeasons = fieldDepsValues.precPlantingSeasons as typeof precPlantingSeasons;
    productAssets = fieldDepsValues.productAssets as Record<string, unknown>;
    plantingEvents = fieldDepsValues.plantingEvents as typeof plantingEvents;

    if (fieldsIds?.length === 0 && !isDmbFieldRetrievalMigrationEnabled) {
      yield call(getFieldsMultiOperation);
    }
  } catch (error) {
    logDmbError(error, {
      appLocation: 'app initializer saga',
    });

    yield put(appInitializerFailure());
    return;
  }

  const {
    feature: { userConfigFeatures: featureFlags },
    field,
  }: { feature: { userConfigFeatures: Record<string, { value?: boolean }> }; field: { id: string; uuid: string } } = yield select();

  const { seasonList, liquidSeasons } = precPlantingSeasons;
  const isPlantingArdDataEnabled = Boolean(featureFlags[FeatureFlagKey.WEB_PLANTING_ARD_DATA]?.value);
  const isHarvestArdDataEnabled = yield select(getHarvestArdDataFeatureSelector);

  if (
    harvestSeasons ||
    plantingSeasons ||
    seasonList ||
    plantingEvents?.plantingSeasons
  ) {
    const harvestSeas = harvestSeasons || [];
    const plantingSeas = plantingSeasons || [];
    const precPlantingSeas = seasonList || [];
    const plantingAnalyticsSeasons = plantingEvents?.plantingSeasons || [];
    const selectorIds = { fieldId, mapLayerIds, seasonIds };

    let seasonsArr: SeasonEntry[] = [];
    if(!isHarvestArdDataEnabled) {
      const mosaicSeasons = extractMosaicSeasons(mosaicData.seasonSpecific);
       seasonsArr = !isPlantingArdDataEnabled ?  [
        ...harvestSeas,
        ...plantingSeas,
        ...precPlantingSeas,
        ...mosaicSeasons,
        ...plantingAnalyticsSeasons,
      ] : [
        ...harvestSeas,
        ...precPlantingSeas,
        ...mosaicSeasons,
      ];

    } else {
      seasonsArr = [
        ...harvestSeas,
        ...precPlantingSeas,
      ];
    }


    const isYearSeasonOnly = ({season}: SeasonEntry) => season.split(':').length === 1;

    const fullSeasonsArray = seasonsArr.filter(season => !isYearSeasonOnly(season));
    const hasSeasonWithSameYear = ({season}: SeasonEntry, lookupYear: string) => season.split(':')[0] === lookupYear;
    const includeYearOnlyIfNoFullVersion = (season: SeasonEntry) => {
      if (isYearSeasonOnly(season)) {
        return !fullSeasonsArray.find(fullSeason => hasSeasonWithSameYear(fullSeason, season.season));
      }

      return true;
    };

    seasonsArr = seasonsArr.filter(includeYearOnlyIfNoFullVersion);

    if (isPlantingArdDataEnabled) {
      const ardPlantingSeasons = plantingAnalyticsSeasons.filter(
        ({season: plantingSeasonTag}: SeasonEntry) => {
          const exists = seasonsArr.find(({season: legacySeasonCode}: SeasonEntry) => getSeasonTagWithWildcards(legacySeasonCode) === plantingSeasonTag);
          return !exists;
      });

      seasonsArr = [
        ...seasonsArr,
        ...ardPlantingSeasons,
      ];
    }

    yield put(receivePlantingAnalytics(plantingEvents.events ?? {}));

    // @TODO: Add Prescription Seasons
    const { seasons, sortedSeasonKeys } = extractSeasons(
      seasonsArr,
      liquidSeasons,
      cropMap,
      { cropSeasonFilterEnabled: Boolean(featureFlags[DMB_CROP_SEASON_FILTER]?.value) }
    );

    const layers = extractMapLayers(
      seasons,
      sortedSeasonKeys,
      fieldPermission,
      mosaicData,
      cropMap,
      productAssets,
      featureFlags
    );

    const requestedIds = determineRequestedSelectorIds(selectorIds);
    const activeVals = getActiveSelectorVals(
      seasons,
      sortedSeasonKeys,
      layers,
      requestedIds
    );
    const { activeMapLayers, activeSeasons } = activeVals;

    const activeSeason = activeSeasons[0].id;
    const activeMapLayer = activeMapLayers[0].id;

    updateSelectorIdsInStorage(
      selectorIds.fieldId,
      activeMapLayer,
      activeSeason
    );

    yield put(
      receiveSelectorVals(seasons, sortedSeasonKeys, layers, activeVals)
    );
    if (mosaicData.seasonAgnostic.soilssurgo) {
      yield put(
        reqSoilSsurgoZones((mosaicData.seasonAgnostic.soilssurgo as { imageId: string }).imageId)
      );
    }

    const useMicrosoftGDD = featureFlags[MICROSOFT_WEATHER_GDD].value;

    yield put(reqActivities(field.uuid));
    yield put(reqObservations(field.id, activeSeason, useMicrosoftGDD));
  } else {

    logDmbError(new Error('Field not found'), {
      mapLayerIds,
      seasonIds,
    });
    yield put(fieldNotFoundFailure());
  }
}

export function* getAppInitializerWatcher(): SagaType {
  yield takeLatest(REQUEST_APP_INITIALIZER, getAppInitializer);
}

export default function* rootSaga(): SagaType {
  yield fork(getCropList);
  yield fork(getAppInitializerWatcher);
}
